import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import {
  ArrowDownCircle, ArrowUpCircle, Gift, Trophy, Target,
  UserPlus, Users, Coins, X
} from 'lucide-react';

const KIND_META = {
  deposit: { icon: ArrowDownCircle, color: '#01FFC3', label: 'Deposit' },
  withdraw: { icon: ArrowUpCircle, color: '#FF6B35', label: 'Withdrawal' },
  bet_placed: { icon: Target, color: '#FF2A6D', label: 'Bet Placed' },
  bet_won: { icon: Trophy, color: '#FFD700', label: 'Bet Won' },
  admin_gift: { icon: Gift, color: '#AB47BC', label: 'Admin Gift' },
  signup_bonus: { icon: UserPlus, color: '#05D9E8', label: 'Signup Bonus' },
  referral_unlocked: { icon: Users, color: '#536DFE', label: 'Referral Unlocked' },
};

export default function WalletLedgerDialog({ open, onClose, userId, endpoint = '/admin/user-ledger' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all / credit / debit

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`${endpoint}/${userId}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [open, userId, endpoint]);

  const filtered = data?.entries?.filter(e => filter === 'all' || e.type === filter) || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="neon-dialog max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span style={{ color: 'var(--neon-primary)' }}>
              <Coins size={18} className="inline mr-2" />
              Wallet History — Last 30 Days
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="text-center py-8" style={{ color: 'var(--neon-text-muted)' }}>Loading...</p>}

        {data && (
          <>
            {/* User Info */}
            <div className="p-3 rounded-lg mb-3" style={{ background: 'var(--neon-surface)' }}>
              <div className="flex justify-between items-center mb-2">
                <div>
                  <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{data.user.name}</p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{data.user.email || data.user.mobile}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Current Balance</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--neon-accent)' }}>₹{data.user.wallet_balance}</p>
                </div>
              </div>
              {(data.user.locked_deposit > 0 || data.user.locked_referral_balance > 0) && (
                <div className="flex gap-3 text-xs pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  {data.user.locked_deposit > 0 && (
                    <span style={{ color: '#FF6B35' }}>🔒 Locked Deposit: ₹{data.user.locked_deposit}</span>
                  )}
                  {data.user.locked_referral_balance > 0 && (
                    <span style={{ color: '#536DFE' }}>🔒 Locked Referral: ₹{data.user.locked_referral_balance}</span>
                  )}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(1,255,195,0.08)' }}>
                <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Total Credit (30d)</p>
                <p className="font-bold" style={{ color: '#01FFC3' }}>+₹{data.summary.total_credit}</p>
              </div>
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(255,107,53,0.08)' }}>
                <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Total Debit (30d)</p>
                <p className="font-bold" style={{ color: '#FF6B35' }}>-₹{data.summary.total_debit}</p>
              </div>
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(5,217,232,0.08)' }}>
                <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Net Flow</p>
                <p className="font-bold" style={{ color: data.summary.net >= 0 ? '#01FFC3' : '#FF6B35' }}>
                  {data.summary.net >= 0 ? '+' : ''}₹{data.summary.net}
                </p>
              </div>
            </div>

            {/* Filter buttons */}
            <div className="flex gap-2 mb-3">
              {[
                { v: 'all', l: 'All' },
                { v: 'credit', l: 'Credit (+)' },
                { v: 'debit', l: 'Debit (-)' },
              ].map(f => (
                <button
                  key={f.v}
                  data-testid={`ledger-filter-${f.v}`}
                  onClick={() => setFilter(f.v)}
                  className="px-3 py-1 rounded-full text-xs font-bold transition-all"
                  style={{
                    background: filter === f.v ? 'var(--neon-primary)' : 'var(--neon-surface)',
                    color: filter === f.v ? '#000' : 'var(--neon-text-muted)',
                  }}
                >
                  {f.l}
                </button>
              ))}
            </div>

            {/* Timeline */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="ledger-entries">
              {filtered.length === 0 && (
                <p className="text-center py-8 text-sm" style={{ color: 'var(--neon-text-muted)' }}>
                  No transactions in this period
                </p>
              )}
              {filtered.map(e => {
                const meta = KIND_META[e.kind] || { icon: Coins, color: 'var(--neon-text)', label: e.kind };
                const Icon = meta.icon;
                const isCredit = e.type === 'credit';
                return (
                  <div
                    key={e.id}
                    className="flex items-start gap-3 p-3 rounded-lg"
                    style={{ background: 'var(--neon-surface)', borderLeft: `3px solid ${meta.color}` }}
                  >
                    <div className="shrink-0 p-2 rounded-full" style={{ background: meta.color + '20' }}>
                      <Icon size={16} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase" style={{ color: meta.color }}>{meta.label}</p>
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--neon-text)' }}>{e.description}</p>
                          {e.source && <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Source: {e.source}</p>}
                          {e.note && <p className="text-[10px] italic" style={{ color: 'var(--neon-text-muted)' }}>{e.note}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold" style={{ color: isCredit ? '#01FFC3' : '#FF6B35' }}>
                            {isCredit ? '+' : '-'}₹{e.amount}
                          </p>
                          <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>
                            {new Date(e.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
